/**
 * Monday.com Deals, Assets, Analytics & Users
 *
 * Extracted domain functions for deals, assets, activity logs, and users.
 * Each function takes a MondayCore instance as its first parameter.
 */

import { logger } from '@/utils/logger';
import type { MondayAsset } from '@/types';
import { mondayClient } from './client';
import {
  DEFAULT_LEAD_COLUMN_MAPPING,
  hubItemToMondayItem,
  type MondayCore,
  type Deal,
  type MondayUser,
  type ParsedActivityData,
  type StageTransition,
  type HubBoardItem,
  type HubActivityLog,
  type HubActivityLogsPage,
  type HubUsersPage,
} from './mondayCore';

// ============================================================================
// Deals
// ============================================================================

/**
 * Get all deals from the Deals board via the Protagona Hub API
 */
export async function getDeals(_core?: MondayCore): Promise<Deal[]> {
  const deals = await mondayClient.get<Deal[]>('/hub/deals', {
    cache: true,
    cacheTTL: 60 * 60 * 1000,
  });
  logger.log(`[Hub] Total deals fetched: ${deals.length}`);
  return deals;
}

// ============================================================================
// Activity Logs / Stage Transitions
// ============================================================================

/**
 * Extract stage label from activity log value
 * Monday.com status columns return objects like: {index: 7, text: 'Stage Name', style: {...}, is_done: false}
 */
export function extractStageLabel(value: unknown): string | null {
  if (!value) return null;

  // If it's already a string, try to parse it as JSON (could be stringified object)
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      // Recursively extract from parsed object
      return extractStageLabel(parsed);
    } catch {
      // Not JSON, return the string as-is
      return value;
    }
  }

  // Handle object values (Monday.com status column format)
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;

    // Monday.com status columns use 'text' property for the label
    if ('text' in obj && typeof obj.text === 'string') {
      return obj.text;
    }

    // Alternative: check for 'label' property (string or nested object)
    if ('label' in obj) {
      const label = obj.label;
      if (typeof label === 'string') return label;
      // Label might be nested object with text
      if (typeof label === 'object' && label !== null) {
        const labelObj = label as Record<string, unknown>;
        if (typeof labelObj.text === 'string') return labelObj.text;
      }
    }
  }

  return null;
}

/**
 * Get activity logs for stage transition tracking
 * Returns stage changes for leads to reconstruct historical funnel
 */
export async function getActivityLogs(
  core: MondayCore,
  options: { boardId?: string; limit?: number; pages?: number } = {}
): Promise<StageTransition[]> {
  const config = core.getConfig();
  const limit = options.limit ?? 100;
  const maxPages = options.pages ?? 10; // Fetch up to 10 pages by default

  const columnMapping = config.columnMappings?.leads ?? DEFAULT_LEAD_COLUMN_MAPPING;
  const stageColumnId = columnMapping['stage'] ?? 'status';

  const allTransitions: StageTransition[] = [];

  // boardId param unused — Hub API uses board name 'deals' directly
  let cursor: string | undefined;
  let pageNum = 0;

  do {
    pageNum++;
    if (pageNum > maxPages) break;

    try {
      logger.log(`[Monday] Fetching activity logs page ${pageNum}...`);
      const url = `/hub/activity/deals?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
      const data = await mondayClient.get<HubActivityLogsPage>(url, {
        cache: pageNum === 1,
        cacheTTL: 60 * 60 * 1000,
      });

      const logs: HubActivityLog[] = data.data ?? [];
      cursor = data.next_cursor ?? undefined;

      if (logs.length === 0) {
        logger.log(`[Monday] No activity logs returned from page ${pageNum}`);
        break;
      }

      logger.log(`[Monday] Processing ${logs.length} activity logs from page ${pageNum}...`);

      if (pageNum === 1 && logs.length > 0) {
        logger.log(`[Monday] Sample activity log:`, logs[0]);
        logger.log(`[Monday] Looking for column_id: ${stageColumnId}`);
      }

      let columnChangeCount = 0;
      let stageChangeCount = 0;
      for (const log of logs) {
        if (log.event !== 'update_column_value') continue;
        columnChangeCount++;

        try {
          const parsedData: ParsedActivityData = JSON.parse(log.data);

          if (columnChangeCount <= 3) {
            logger.log(`[Monday] Column change found: column_id="${parsedData.column_id}", looking for "${stageColumnId}"`);
          }

          if (parsedData.column_id !== stageColumnId) continue;
          stageChangeCount++;

          const fromStage = extractStageLabel(parsedData.previous_value);
          const toStage = extractStageLabel(parsedData.value);

          if (!toStage) continue;

          allTransitions.push({
            itemId: parsedData.pulse_id?.toString() ?? '',
            itemName: parsedData.pulse_name?.toString() ?? '',
            fromStage,
            toStage,
            timestamp: log.created_at,
            userId: log.user_id,
            userName: undefined,
          });
        } catch {
          continue;
        }
      }

      logger.log(`[Monday] Page ${pageNum}: ${logs.length} logs, ${columnChangeCount} column changes, ${stageChangeCount} stage changes`);

      if (logs.length < limit) {
        logger.log(`[Monday] Reached end of activity logs`);
        break;
      }
    } catch (error) {
      logger.simpleError(`[Monday] Error fetching activity logs page ${pageNum}:`, error);
      break;
    }
  } while (cursor);

  logger.log(`[Monday] Total stage transitions found: ${allTransitions.length}`);

  // Sort by timestamp (newest first for easier processing)
  return allTransitions.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

// ============================================================================
// Assets
// ============================================================================

/**
 * Get content assets from Monday board
 */
export async function getAssets(core: MondayCore): Promise<MondayAsset[]> {
  const config = core.getConfig();

  if (!config.assetsBoardId) {
    logger.simpleWarn('[Monday] Assets board not configured');
    return [];
  }

  logger.log(`[Monday] Fetching assets from board ${config.assetsBoardId}`);

  const allItems = await mondayClient.get<HubBoardItem[]>('/hub/assets', {
    cache: true,
    cacheTTL: 60 * 60 * 1000,
  });
  const items = Array.isArray(allItems) ? allItems.map(hubItemToMondayItem) : [];
  logger.log(`[Monday] Fetched ${items.length} assets`);

  // Map column IDs - adjust these based on your board structure
  const columnMappings = config.columnMappings?.assets ?? {
    type: 'asset_type',
    status: 'status',
    description: 'description',
    industry: 'industry',
    tags: 'tags',
    fileUrl: 'file_url',
    previewUrl: 'preview_url',
    sourceUrl: 'source_url',
    owner: 'owner',
    targetPersona: 'target_persona',
    buyerStage: 'buyer_stage',
  };

  return items.map((item) => {
    const getValue = (columnId: string): string => {
      const col = item.column_values.find((c) => c.id === columnId);
      return col?.text ?? '';
    };

    const getArrayValue = (columnId: string): string[] => {
      const value = getValue(columnId);
      if (!value) return [];
      // Handle comma-separated values or JSON arrays
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        return value.split(',').map((v) => v.trim()).filter(Boolean);
      }
    };

    const getStatus = (): 'Published' | 'Draft' | 'Active' | 'Archived' => {
      const status = getValue(columnMappings.status ?? 'status');
      if (status.toLowerCase().includes('published')) return 'Published';
      if (status.toLowerCase().includes('active')) return 'Active';
      if (status.toLowerCase().includes('archived')) return 'Archived';
      return 'Draft';
    };

    const getAssetType = (): string => {
      const type = getValue(columnMappings.type ?? 'asset_type');
      const name = item.name?.toLowerCase() ?? '';

      // Check the name for common type indicators
      if (name.includes('1-pager') || name.includes('one-pager') || name.includes('1 pager')) return '1-Pager';
      if (name.includes('case study')) return 'Case Study';

      if (!type) return 'Campaign Asset';
      // Normalize common types
      const lowerType = type.toLowerCase();
      if (lowerType.includes('1-pager') || lowerType.includes('one-pager') || lowerType.includes('1 pager')) return '1-Pager';
      if (lowerType.includes('case study')) return 'Case Study';
      if (lowerType.includes('whitepaper') || lowerType.includes('white paper')) return 'Whitepaper';
      if (lowerType.includes('video')) return 'Video';
      if (lowerType.includes('template')) return 'Template';
      if (lowerType.includes('blog')) return 'Blog Post';
      if (lowerType.includes('presentation')) return 'Presentation';
      if (lowerType.includes('infographic')) return 'Infographic';
      if (lowerType.includes('report')) return 'Report';
      if (lowerType.includes('interactive') || lowerType.includes('tool')) return 'Interactive Tool';
      return type; // Return original if no match
    };

    // Get file URL or link value
    const getFileUrl = (): string | null => {
      // Try multiple possible column IDs for files/links
      const possibleColumns = ['file_url', 'file', 'link', 'url', 'files'];
      for (const colId of possibleColumns) {
        const col = item.column_values.find((c) => c.id === colId);
        if (col?.text) return col.text;
        // Check if it's a file column with value
        if (col?.value) {
          try {
            const parsed = JSON.parse(col.value);
            if (parsed?.files?.[0]?.url) return parsed.files[0].url;
            if (parsed?.url) return parsed.url;
          } catch {
            // Not JSON, use text
          }
        }
      }
      return getValue(columnMappings.fileUrl ?? 'file_url') || null;
    };

    // Generate thumbnail emoji based on type
    const getThumbnail = (type: string): string => {
      const typeEmojis: Record<string, string> = {
        'Case Study': '📊',
        '1-Pager': '📝',
        'Whitepaper': '📄',
        'Video': '🎬',
        'Template': '📋',
        'Blog Post': '✍️',
        'Presentation': '📽️',
        'Infographic': '📈',
        'Report': '📑',
        'Interactive Tool': '🛠️',
        'Campaign Asset': '📦',
      };
      return typeEmojis[type] ?? '📁';
    };

    const assetType = getAssetType();
    const createdAt = item.created_at ? new Date(item.created_at).toISOString().split('T')[0] ?? '' : '';
    const updatedAt = item.updated_at ? new Date(item.updated_at).toISOString().split('T')[0] ?? '' : '';

    return {
      id: item.id,
      name: item.name,
      type: assetType,
      status: getStatus(),
      industry: getArrayValue(columnMappings.industry ?? 'industry'),
      tags: getArrayValue(columnMappings.tags ?? 'tags'),
      description: getValue(columnMappings.description ?? 'description'),
      metrics: {
        downloads: 0,
        shares: 0,
        conversionRate: 0,
      },
      targetPersona: getArrayValue(columnMappings.targetPersona ?? 'target_persona'),
      buyerStage: getArrayValue(columnMappings.buyerStage ?? 'buyer_stage'),
      fileUrl: getFileUrl(),
      previewUrl: getValue(columnMappings.previewUrl ?? 'preview_url') || getFileUrl() || undefined,
      sourceUrl: getValue(columnMappings.sourceUrl ?? 'source_url') || undefined,
      thumbnail: getThumbnail(assetType),
      createdDate: createdAt,
      lastUpdated: updatedAt,
      owner: getValue(columnMappings.owner ?? 'owner') || 'Unassigned',
      relatedCampaigns: [],
      // Source tracking
      source: 'monday' as const,
      sourceId: item.id,
      sourceBoardId: config.assetsBoardId,
    } satisfies MondayAsset;
  });
}

// ============================================================================
// Users
// ============================================================================

/**
 * Get all workspace users with profile photos
 * Cached for 30 minutes (user data rarely changes)
 */
export async function getUsers(_core?: MondayCore): Promise<MondayUser[]> {
  try {
    const allUsers: MondayUser[] = [];
    let userCursor: string | undefined;
    do {
      const url = `/hub/users?limit=100${userCursor ? `&cursor=${encodeURIComponent(userCursor)}` : ''}`;
      const page = await mondayClient.get<HubUsersPage>(url, {
        cache: !userCursor,
        cacheTTL: 30 * 60 * 1000,
      });
      allUsers.push(...page.data.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
        enabled: u.enabled,
        title: u.title ?? undefined,
        photo_thumb: u.photo_thumb ?? undefined,
        photo_small: u.photo_small ?? undefined,
        photo_original: u.photo_original ?? undefined,
      })));
      userCursor = page.next_cursor ?? undefined;
    } while (userCursor);

    return allUsers.filter(user => user.enabled);
  } catch (error) {
    logger.simpleError('[Monday] Error fetching users:', error);
    return [];
  }
}
